import { DialogTitle } from "@mui/material";
import { orderBusiness } from "api/bussiness/order/order-bussiness";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { IAddOrder } from "mobx-stores/_models/orders/order";
import { useStores } from "mobx-stores/_root-store";
import { useResetCustomerDependentControls } from "pages/common/hooks/use-reset-customer-dependent-controls";
import { useCallback, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getAddOrderFormSchema } from "../schema";

export const AddOrder = observer(() => {
  const {
    orderManagement: { openNew, getOrders, setProp },
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    submitHandler,
  } = useForm<IAddOrder>((form) => getAddOrderFormSchema(form), {
    poDate: dateTimeToUTCString(dayjs()),
    poNumber: "",
    customerId: "",
    items: [
      {
        compensatingQuantity: 0,
        compensatingReason: "",
        deliveryDate: dateTimeToUTCString(dayjs()),
        isCompensating: false,
        productId: "",
        quantityLower: 0,
        quantityStandard: 0,
        quantityUpper: 0,
        unitPrice: 0,
      },
    ],
  });

  const saveHandler = useCallback(
    async (data: IAddOrder) => {
      try {
        await orderBusiness.add(data);
        await getOrders();
        setProp("openNew", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getOrders, setProp],
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const customerControl = useMemo(() => {
    return getControl("customerId");
  }, [getControl]);

  useResetCustomerDependentControls({
    customerId: customerControl.value,
    arrayChildControls: [
      {
        arrayControl: itemControl,
        childControlName: "productId",
      },
    ],
  });

  const orderItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const removeRowHandler = useCallback(
    (idx: number) => {
      itemControl.removeChildByIndex(idx);
    },
    [itemControl],
  );

  const addNewOrderItemRow = useCallback(() => {
    itemControl.addNewChild();
  }, [itemControl]);

  const onOrderItemsValueChangedHandler = useCallback(
    (ctr: IControl, rowIdx: number) => {
      if (ctr.name === "isCompensating") {
        const item = orderItems[rowIdx];
        if (item) {
          const compensatingQuantityCtr = item.filter(
            (x) => x.name === "compensatingQuantity",
          )?.[0];
          if (compensatingQuantityCtr) {
            compensatingQuantityCtr.setProp("disabled", !ctr.value);
          }
          const compensatingReasonCtr = item.filter(
            (x) => x.name === "compensatingReason",
          )?.[0];
          if (compensatingReasonCtr) {
            compensatingReasonCtr.setProp("disabled", !ctr.value);
          }
        }
      }
    },
    [orderItems],
  );

  return (
    <DialogForm
      open={openNew}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openNew", false)}
      onCancelBtnClicked={() => setProp("openNew", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Thêm đơn đặt hàng</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm controls={controls}></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer
          label={itemControl.label}
          addNewRow={addNewOrderItemRow}
        >
          {orderItems && orderItems.length > 0 && (
            <DynamicFormArray
              onValueChanged={onOrderItemsValueChangedHandler}
              containerProps={{
                sx: {
                  minWidth: "1250px",
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              children={orderItems}
              onRemoveRowClick={removeRowHandler}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
